import { Dispatch, SetStateAction, useEffect, useState } from 'react';
import { useUpdateEffect } from 'react-use';
import isUndefined from 'lodash/isUndefined';
import noop from 'utils/noop';

interface UseCombinedState<TValue = string> {
  internalValue?: TValue;
  externalValue?: TValue;
  setInternalValue?: ((value?: TValue) => void) | Dispatch<SetStateAction<TValue | undefined>>;
  setExternalValue?: ((value?: TValue) => void) | Dispatch<SetStateAction<TValue | undefined>>;
}

/**
 * This makes sure that external changes and internal changes remain in sync.
 *
 * IE:
 *    - If an external prop is changed, the internal state will be updated.
 *    - If internal state is changed, a callback will fire allowing the
 *        external prop to be updated if required.
 */
export default function useCombinedState<TValue = string>({
  internalValue,
  externalValue,
  setInternalValue = noop,
  setExternalValue = noop,
}: UseCombinedState<TValue>): [TValue | undefined, Dispatch<SetStateAction<TValue | undefined>>] {
  const [value, setValue] = useState(!isUndefined(externalValue) ? externalValue : internalValue);

  useEffect(() => {
    if (internalValue !== value) {
      setInternalValue(value);
    }
    if (externalValue !== value) {
      setExternalValue(value);
    }
  }, [value]);

  // When external prop changes, update all other values
  useUpdateEffect(() => {
    if (externalValue !== value) {
      setValue(externalValue);
    }
  }, [externalValue]);

  // When internal state changes, update all other values
  useUpdateEffect(() => {
    if (internalValue !== value) {
      setValue(internalValue);
    }
  }, [internalValue]);

  return [value, setValue];
}
